import { useState, useEffect } from 'react';
import { Search, MessageSquare, MessageSquarePlus, ArrowLeft, Phone, RefreshCw, Settings2, AlertCircle } from 'lucide-react';
import { useWhatsappChats, useWhatsappWebMutations, type WhatsappChat } from '../whatsappWebApi';
import { Spinner } from '@/shared/components/ui/Spinner';
import { cn } from '@/shared/utils/utils';
import { api } from '@/shared/api/axios';

interface ChatSidebarProps {
  selectedChat: string | null;
  onSelectChat: (id: string) => void;
  me: { number: string; pushname: string; picUrl?: string } | null;
  /** Shown to admins: opens the inbox settings. */
  onOpenSettings?: () => void;
  /** false = this user sees only their own clients' conversations. */
  seesAll?: boolean;
}

interface SearchedContact {
  id: string;
  name: string;
  phone: string;
  company: string;
}

function formatChatTime(ts: number | null | undefined): string {
  if (!ts) return '';
  const date = new Date(ts < 10000000000 ? ts * 1000 : ts);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function LastTick({ chat }: { chat: WhatsappChat }) {
  if (!chat.lastFromMe) return null;
  if (chat.lastStatus === 'failed') return <AlertCircle className="mr-1 inline h-3.5 w-3.5 shrink-0 text-danger-text" aria-label="Not delivered" />;
  const double = chat.lastStatus === 'delivered' || chat.lastStatus === 'read';
  return (
    <svg viewBox="0 0 16 11" width="16" height="11" className={cn('mr-1 inline shrink-0', chat.lastStatus === 'read' ? 'text-[#53bdeb]' : 'text-[#8696a0]')} aria-hidden>
      <path fill="currentColor" d="M11.07.65 4.6 7.12 1.93 4.45.88 5.5l3.72 3.72 7.52-7.52z" />
      {double && <path fill="currentColor" d="M15.07.65 8.6 7.12l-.6-.6-1.05 1.05 1.65 1.65 7.52-7.52z" />}
    </svg>
  );
}

export function ChatSidebar({ selectedChat, onSelectChat, me, onOpenSettings, seesAll = true }: ChatSidebarProps) {
  const [search, setSearch] = useState('');
  const [onlyUnread, setOnlyUnread] = useState(false);
  const { data: chats = [], isLoading, isFetching } = useWhatsappChats(true);
  const { refreshChats } = useWhatsappWebMutations();
  const [isNewChatOpen, setIsNewChatOpen] = useState(false);
  const [newChatNumber, setNewChatNumber] = useState('');
  const [newChatSearch, setNewChatSearch] = useState('');
  const [searchedCrmContacts, setSearchedCrmContacts] = useState<SearchedContact[]>([]);
  const [isSearchingCrm, setIsSearchingCrm] = useState(false);

  // CRM contacts matching the sidebar search (or the new-chat search).
  const crmQuery = (isNewChatOpen ? newChatSearch : search).trim();
  useEffect(() => {
    if (crmQuery.length < 2) { setSearchedCrmContacts([]); return; }
    const timer = setTimeout(async () => {
      try {
        setIsSearchingCrm(true);
        const res = await api.get<{ contacts: SearchedContact[] }>(`/api/whatsapp-web/search-contacts?q=${encodeURIComponent(crmQuery)}`);
        setSearchedCrmContacts(res.data?.contacts || []);
      } catch {
        setSearchedCrmContacts([]);
      } finally {
        setIsSearchingCrm(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [crmQuery]);

  const q = search.toLowerCase();
  const filteredChats = chats.filter((c) =>
    (!onlyUnread || c.unreadCount > 0)
    && ((c.name || '').toLowerCase().includes(q) || (c.id || '').includes(q.replace(/\D/g, '') || '~') || (c.lastMessage || '').toLowerCase().includes(q)),
  );
  const unreadTotal = chats.filter((c) => c.unreadCount > 0).length;
  const newChatDigits = newChatNumber.replace(/\D/g, '');

  const openNewChat = (id: string) => {
    onSelectChat(id);
    setIsNewChatOpen(false);
    setNewChatNumber('');
    setNewChatSearch('');
  };

  return (
    <div className="relative flex h-full flex-col overflow-hidden">
      <div className="box-border flex h-[59px] shrink-0 items-center justify-between bg-[#f0f2f5] px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#00a884] text-white">
            <span className="text-body-lg font-semibold">{me?.pushname?.charAt(0)?.toUpperCase() || 'R'}</span>
          </div>
          <div className="min-w-0">
            <p className="truncate text-[14px] font-medium text-[#111b21]">Team Inbox</p>
            <p className="truncate text-[12px] text-[#667781]">{me?.number || 'WhatsApp Business'}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" className="flex h-10 w-10 items-center justify-center rounded-full text-[#54656f] hover:bg-black/5" onClick={() => refreshChats()} title="Refresh">
            <RefreshCw className={cn('h-[20px] w-[20px]', isFetching && 'animate-spin')} />
          </button>
          <button type="button" className="flex h-10 w-10 items-center justify-center rounded-full text-[#54656f] hover:bg-black/5" onClick={() => setIsNewChatOpen(true)} title="New chat">
            <MessageSquarePlus className="h-[22px] w-[22px]" />
          </button>
          {onOpenSettings && (
            <button type="button" className="flex h-10 w-10 items-center justify-center rounded-full text-[#54656f] hover:bg-black/5" onClick={onOpenSettings} title="Inbox settings">
              <Settings2 className="h-[21px] w-[21px]" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 border-b border-[#e9edef] bg-white p-2">
        <div className="flex items-center rounded-lg bg-[#f0f2f5] px-3 py-1.5">
          <Search className="h-[18px] w-[18px] shrink-0 text-[#54656f]" />
          <input
            type="text"
            placeholder="Search chats or contacts"
            className="ml-3 w-full bg-transparent text-[15px] text-[#111b21] outline-none placeholder:text-[#54656f]"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isSearchingCrm && !isNewChatOpen && <Spinner className="ml-2 h-4 w-4 shrink-0 text-[#00a884]" />}
        </div>
        <div className="flex gap-2">
          {[{ v: false, label: 'All' }, { v: true, label: `Unread${unreadTotal ? ` · ${unreadTotal}` : ''}` }].map((t) => (
            <button
              key={t.label}
              type="button"
              onClick={() => setOnlyUnread(t.v)}
              className={cn('rounded-full px-3 py-1 text-[13px]', onlyUnread === t.v ? 'bg-[#d9fdd3] font-medium text-[#008069]' : 'bg-[#f0f2f5] text-[#54656f] hover:bg-[#e9edef]')}
            >
              {t.label}
            </button>
          ))}
        </div>
        {!seesAll && <p className="px-1 text-[12px] text-[#667781]">Showing chats with your own clients.</p>}
      </div>

      <div className="flex-1 overflow-y-auto bg-white">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center"><Spinner className="text-[#00a884]" /></div>
        ) : (
          <>
            {filteredChats.map((chat) => {
              const initial = (chat.name || '').replace(/^\+/, '').charAt(0).toUpperCase() || 'C';
              return (
                <button
                  key={chat.id}
                  type="button"
                  onClick={() => onSelectChat(chat.id)}
                  className={cn('flex w-full items-center gap-3 px-3 text-left hover:bg-[#f5f6f6]', selectedChat === chat.id && 'bg-[#f0f2f5] hover:bg-[#f0f2f5]')}
                >
                  <div className="flex h-[49px] w-[49px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#dfe5e7] text-[#54656f]">
                    <span className="text-body-lg font-medium">{/\d/.test(initial) ? '#' : initial}</span>
                  </div>
                  <div className="flex h-[72px] min-w-0 flex-1 flex-col justify-center border-b border-[#f2f2f2]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-[16px] text-[#111b21]">{chat.name || chat.id}</span>
                      <span className={cn('shrink-0 text-[12px]', chat.unreadCount > 0 ? 'font-medium text-[#00a884]' : 'text-[#667781]')}>{formatChatTime(chat.timestamp)}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center text-[14px] text-[#667781]">
                        <LastTick chat={chat} />
                        <span className="truncate">{chat.lastMessage || 'No messages yet'}</span>
                      </span>
                      {chat.unreadCount > 0 && (
                        <span className="flex h-[20px] min-w-[20px] shrink-0 items-center justify-center rounded-full bg-[#00a884] px-[0.4rem] text-[11px] font-bold leading-none text-white">
                          {chat.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}

            {search.trim().length >= 2 && searchedCrmContacts.length > 0 && (
              <div className="mt-2 border-t border-[#e9edef] pt-2">
                <div className="bg-[#f8faf9] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#008069]">
                  Contacts ({searchedCrmContacts.length})
                </div>
                {searchedCrmContacts.map((c) => (
                  <button key={`crm-${c.id}`} type="button" onClick={() => { onSelectChat(c.id); setSearch(''); }} className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-[#f5f6f6]">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e8f5e9] text-sm font-medium text-[#008069]">{(c.name || 'C').charAt(0).toUpperCase()}</div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-[14.5px] font-medium text-[#111b21]">{c.name}</span>
                      <span className="flex items-center gap-1 text-xs text-[#667781]"><Phone className="inline h-3 w-3" /> {c.phone}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {filteredChats.length === 0 && searchedCrmContacts.length === 0 && (
              <div className="flex h-48 flex-col items-center justify-center p-4 text-center text-[#54656f]">
                <MessageSquare className="mb-2 h-8 w-8 text-[#00a884] opacity-40" />
                <p className="text-[14px] font-medium text-[#111b21]">{onlyUnread ? 'No unread chats' : 'No chats found'}</p>
                {!onlyUnread && (
                  <button type="button" onClick={() => setIsNewChatOpen(true)} className="mt-4 rounded-full bg-[#00a884] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#017561]">
                    + Start a new chat
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {isNewChatOpen && (
        <div className="absolute inset-0 z-30 flex flex-col bg-white">
          <div className="flex h-[64px] items-center gap-4 bg-[#008069] px-4 text-white">
            <button type="button" onClick={() => setIsNewChatOpen(false)} className="rounded-full p-1 hover:bg-white/10" aria-label="Close">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h2 className="text-[18px] font-medium">New chat</h2>
          </div>
          <div className="flex flex-col gap-3 border-b border-[#e9edef] p-4">
            <label className="text-[13px] text-[#54656f]" htmlFor="wa-new-number">Mobile number</label>
            <div className="flex gap-2">
              <input
                id="wa-new-number"
                type="tel"
                inputMode="tel"
                placeholder="10-digit mobile number"
                value={newChatNumber}
                onChange={(e) => setNewChatNumber(e.target.value)}
                className="min-w-0 flex-1 rounded-md border border-[#e9edef] px-3 py-2 text-[14px] outline-none focus:border-[#00a884]"
              />
              <button
                type="button"
                disabled={newChatDigits.length < 10}
                onClick={() => openNewChat(newChatDigits.slice(-10))}
                className="rounded-md bg-[#00a884] px-4 py-2 text-[14px] font-medium text-white hover:bg-[#017561] disabled:opacity-50"
              >
                Open
              </button>
            </div>
            <label className="mt-1 text-[13px] text-[#54656f]" htmlFor="wa-new-search">Or find a contact</label>
            <input
              id="wa-new-search"
              type="text"
              placeholder="Name, company or number"
              value={newChatSearch}
              onChange={(e) => setNewChatSearch(e.target.value)}
              className="rounded-md border border-[#e9edef] px-3 py-2 text-[14px] outline-none focus:border-[#00a884]"
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {isSearchingCrm && <div className="flex justify-center py-4"><Spinner className="text-[#00a884]" /></div>}
            {searchedCrmContacts.map((c) => (
              <button key={`new-${c.id}`} type="button" onClick={() => openNewChat(c.id)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-[#f5f6f6]">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e8f5e9] text-sm font-medium text-[#008069]">{(c.name || 'C').charAt(0).toUpperCase()}</div>
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-[14.5px] font-medium text-[#111b21]">{c.name}</span>
                  <span className="text-xs text-[#667781]">{c.phone}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
